import { createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";

export interface PlaceholderConfig {
    options?: string[];
    allowCustom?: boolean;
}

export interface PlaceholdersData {
    [key: string]: PlaceholderConfig;
}

function createInteractivePromptModel() {
    const promptInitialized = createEvent<{ prompt: string; placeholders: PlaceholdersData }>();
    const placeholderValueChanged = createEvent<{ key: string; value: string }>();

    const $placeholderValues = createStore<Record<string, string>>({});
    const $finalPrompt = createStore<string>("");
    const $originalPrompt = createStore<string>("");
    const $placeholders = createStore<PlaceholdersData>({});

    sample({
        clock: promptInitialized,
        fn: ({ prompt }) => prompt,
        target: $originalPrompt
    });

    sample({
        clock: promptInitialized,
        fn: ({ placeholders }) => placeholders,
        target: $placeholders
    });

    sample({
        clock: promptInitialized,
        fn: ({ prompt, placeholders }) => {
            const regex = /\{\{([^}]+)\}\}/g;
            const matches = [...prompt.matchAll(regex)];
            const keys = matches.map((m) => m[1].trim());

            const initial: Record<string, string> = {};
            keys.forEach((key) => {
                initial[key] = "";
            });
            return initial;
        },
        target: $placeholderValues
    });

    sample({
        clock: placeholderValueChanged,
        source: $placeholderValues,
        fn: (values, { key, value }) => ({ ...values, [key]: value }),
        target: $placeholderValues
    });

    sample({
        source: { prompt: $originalPrompt, values: $placeholderValues },
        fn: ({ prompt, values }) => {
            let result = prompt;
            Object.entries(values).forEach(([key, value]) => {
                result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
            });
            return result;
        },
        target: $finalPrompt
    });

    return new ModelContract(
        {
            $placeholderValues,
            $finalPrompt,
            $originalPrompt,
            $placeholders
        },
        {
            promptInitialized,
            placeholderValueChanged
        },
        {},
        {}
    );
}
export class InteractivePromptModelModule extends ModuleDescription<
    ModelContract<
        {
            $placeholderValues: any;
            $finalPrompt: any;
            $originalPrompt: any;
            $placeholders: any;
        },
        any,
        any,
        any
    >
> {
    private _defaultContract: ModelContract<any, any, any, any> | null = null;

    constructor() {
        super();
    }

    createInstance() {
        return createInteractivePromptModel();
    }

    getDefaultContract() {
        if (!this._defaultContract) {
            this._defaultContract = createInteractivePromptModel();
        }
        return this._defaultContract;
    }

    run() {}
}
